import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('loading');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const pollStatus = async (attempts = 0) => {
      if (attempts >= 5) {
        setStatus('timeout');
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollStatus(attempts + 1), 2000);
      } catch (error) {
        setStatus('error');
      }
    };

    if (sessionId && token) {
      pollStatus();
    }
  }, [sessionId, token]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-md"
      >
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#c00000] border-t-transparent rounded-full animate-spin" />
            <h1 className="font-heading text-3xl text-white mb-4">Processing Payment...</h1>
            <p className="font-body text-white/50">Please wait while we confirm your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-[#c00000]/10 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#c00000]" />
            </div>
            <h1 className="font-heading text-3xl text-white mb-4">Payment Successful!</h1>
            <p className="font-body text-white/50 mb-8">
              Thank you for your order. You will receive a confirmation email shortly.
            </p>
            <div className="flex flex-col gap-4">
              <Link to="/profile">
                <Button className="w-full bg-[#c00000] text-white rounded-none py-4 font-heading" data-testid="view-orders-btn">
                  VIEW MY ORDERS
                </Button>
              </Link>
              <Link to="/shop">
                <Button variant="outline" className="w-full bg-transparent border-white/10 text-white rounded-none py-4 font-heading">
                  CONTINUE SHOPPING
                </Button>
              </Link>
            </div>
          </>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-white/5 rounded-full flex items-center justify-center">
              <Package className="w-10 h-10 text-white/30" />
            </div>
            <h1 className="font-heading text-3xl text-white mb-4">
              {status === 'expired' ? 'Payment Expired' : 'Something Went Wrong'}
            </h1>
            <p className="font-body text-white/50 mb-8">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t confirm your payment. Please check your orders or contact support.'
              }
            </p>
            <div className="flex flex-col gap-4">
              <Link to="/profile">
                <Button className="w-full bg-[#c00000] text-white rounded-none py-4 font-heading">
                  CHECK MY ORDERS
                </Button>
              </Link>
              <Link to="/cart">
                <Button variant="outline" className="w-full bg-transparent border-white/10 text-white rounded-none py-4 font-heading">
                  BACK TO CART
                </Button>
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default CheckoutSuccessPage;
